import React, { useState } from 'react';
import { Image as ImageIcon, AlertCircle } from 'lucide-react';

export interface MediaWidgetConfig {
  sourceType: 'upload' | 'url';
  url?: string; // URL or base64 data
  mediaType: 'image' | 'video';
  fit: 'contain' | 'cover' | 'fill';
  altText?: string;
  backgroundColor?: string;
}

interface MediaWidgetProps {
  config: MediaWidgetConfig;
  width: number; // Grid columns (1-12)
  height: number; // Grid rows
}

// Helper to convert YouTube URL to embed URL
function getYouTubeEmbedUrl(url: string): string | null {
  if (!url) return null;

  // Match various YouTube URL formats
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  const videoId = (match && match[7].length === 11) ? match[7] : null;

  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }

  return null;
}

// Helper to check if URL is a YouTube URL
function isYouTubeUrl(url: string): boolean {
  return url.includes('youtube.com') || url.includes('youtu.be');
}

export function MediaWidget({ config, width, height }: MediaWidgetProps) {
  const {
    url = '',
    mediaType = 'image',
    fit = 'contain',
    altText = '',
    backgroundColor,
  } = config || {};

  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Dynamic sizing
  const area = width * height;
  const isSmall = area <= 12;
  const padding = isSmall ? 'p-2' : 'p-3';

  // Fit classes
  const fitClass = {
    contain: 'object-contain',
    cover: 'object-cover',
    fill: 'object-fill',
  }[fit];

  // Check if this is a YouTube video
  const isYouTube = mediaType === 'video' && isYouTubeUrl(url);
  const embedUrl = isYouTube ? getYouTubeEmbedUrl(url) : null;

  // No media state
  if (!url) {
    return (
      <div
        className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} flex flex-col items-center justify-center`}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <ImageIcon size={48} className="text-gray-300 dark:text-gray-600 mb-2" />
        <div className="text-gray-400 dark:text-gray-500 text-sm text-center">
          No media selected
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div
        className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} flex flex-col items-center justify-center`}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <AlertCircle size={48} className="text-red-400 mb-2" />
        <div className="text-red-600 dark:text-red-400 text-sm text-center">
          Failed to load media
        </div>
        <button
          onClick={() => {
            setHasError(false);
            setIsLoading(true);
          }}
          className="mt-2 text-xs text-purple-600 dark:text-purple-400 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  // Image display
  if (mediaType === 'image') {
    return (
      <div
        className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm overflow-hidden flex items-center justify-center`}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
            <div className="text-gray-400 dark:text-gray-500 text-sm">Loading...</div>
          </div>
        )}
        <img
          src={url}
          alt={altText || 'Media widget image'}
          className={`w-full h-full ${fitClass}`}
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        />
      </div>
    );
  }

  // Video display
  if (mediaType === 'video') {
    // YouTube embed
    if (isYouTube && embedUrl) {
      return (
        <div
          className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm overflow-hidden`}
          style={{ backgroundColor: backgroundColor || undefined }}
        >
          <iframe
            src={embedUrl}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="YouTube video"
          />
        </div>
      );
    }

    // Regular video file
    return (
      <div
        className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm overflow-hidden flex items-center justify-center`}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <video
          src={url}
          className={`w-full h-full ${fitClass}`}
          controls
          onLoadedData={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        >
          Your browser does not support video playback.
        </video>
      </div>
    );
  }

  return null;
}
